import {useState} from "react";
import {formatISO, getDay, parseISO} from "date-fns";
import useCachedPrices from "../RatePlanner/useCachedPrices";

export const INDETERMINATE = "INDETERMINATE";


const usePriceSummarizer = () => {
    const [rateConfig, setRateConfig] = useState({})
    const getCachedPrices = useCachedPrices();

    const resetRateConfig = () => {
        setRateConfig({});
    }

    // Load the rate config from the query cache
    const fromQueryCache = (apartmentId, rateId, dateStart, dateEnd) => {
        // Take the difference between the dates and divide by milliseconds per day.
        // Round to the nearest whole number to deal with DST.
        // +1 because the end date is included
        const numberOfDays = Math.round((dateEnd - dateStart) / (1000 * 60 * 60 * 24)) + 1

        const rateData = getCachedPrices(apartmentId, rateId, dateStart, dateEnd);

        const dateISO = rateData.timestamps;
        const dayNumbers = rateData.timestamps.map(t => getDay(parseISO(t)));
        const dateISOStart = formatISO(dateStart, {representation: "date"});
        const dateISOEnd = formatISO(dateEnd, {representation: "date"});

        const indexStart = dateISO.findIndex(t => t === dateISOStart);
        const indexEnd = dateISO.findIndex(t => t === dateISOEnd);

        // Given an array of values, return the value oat index indexStart if all the values are the same
        // in the range of dates specified by indexStart and indexEnd, otherwise return null
        const uniqueValueOrNull = (arr, day=null) => {
            if (!(indexStart >= 0)){
                return null;
            }
            if (!(indexEnd >= indexStart)){
                return null;
            }
            if (indexEnd - indexStart !== numberOfDays - 1) {
                return null;
            }
            if (arr.length !== rateData.timestamps.length) {
                return null;
            }

            // Filter by date range and day of week
            const filtered = arr.filter((_, i) => (
                i >= indexStart && i <= indexEnd  // filter by date range
                && (day === null || dayNumbers[i] === day))  // filter by day of week
            );

            // Check equality
            if (filtered.length > 0) {
                return filtered.every(v => v === filtered[0]) ? filtered[0] : INDETERMINATE;
            }

            return null;
        }

        const rateConfig = {
            enabled: uniqueValueOrNull(rateData.enabled),
            minNights: uniqueValueOrNull(rateData.minNights),
            minPeople: uniqueValueOrNull(rateData.minPeople),
            checkin: {
                'sunday': uniqueValueOrNull(rateData.checkin, 0),
                'monday': uniqueValueOrNull(rateData.checkin, 1),
                'tuesday': uniqueValueOrNull(rateData.checkin, 2),
                'wednesday': uniqueValueOrNull(rateData.checkin, 3),
                'thursday': uniqueValueOrNull(rateData.checkin, 4),
                'friday': uniqueValueOrNull(rateData.checkin, 5),
                'saturday': uniqueValueOrNull(rateData.checkin, 6),
            },
            checkout: {
                'sunday': uniqueValueOrNull(rateData.checkout, 0),
                'monday': uniqueValueOrNull(rateData.checkout, 1),
                'tuesday': uniqueValueOrNull(rateData.checkout, 2),
                'wednesday': uniqueValueOrNull(rateData.checkout, 3),
                'thursday': uniqueValueOrNull(rateData.checkout, 4),
                'friday': uniqueValueOrNull(rateData.checkout, 5),
                'saturday': uniqueValueOrNull(rateData.checkout, 6),
            },
            pricesCent: Object.keys(rateData.pricesCent).reduce((acc, people) => {
                acc[people] = uniqueValueOrNull(rateData.pricesCent[people]);
                return acc;
            }, {})
        };

        setRateConfig(rateConfig);
        return rateConfig;
    }

    return [rateConfig, fromQueryCache, resetRateConfig];
}

export default usePriceSummarizer;